<template>
  <teleport to="body">
    <AppModal
      :isVisible="isVisible"
      :title="$t('provisioning.requestTitle')"
      @cancel="handleCancel"
    >
      <form @submit.prevent="handleSubmit" class="finance-form">
        <div class="form-group">
          <label>{{ $t('provisioning.amount') }} <span class="required">*</span></label>
          <input
            type="number"
            v-model="formData.requestedAmount"
            step="0.01"
            min="0.01"
            required
            class="form-input"
            :placeholder="$t('provisioning.amountPlaceholder')"
          />
        </div>
        
        <div class="form-group">
          <label>{{ $t('provisioning.provisionDeadline') }} <span class="required">*</span></label>
          <input
            type="date"
            v-model="formData.provisionDeadline"
            required
            class="form-input"
            :min="minDate"
          />
        </div>
        
        <div class="form-group">
          <label>{{ $t('provisioning.description') }} <span class="required">*</span></label>
          <textarea
            v-model="formData.description"
            class="form-input"
            required
            maxlength="1000"
            rows="4"
            :placeholder="$t('provisioning.descriptionPlaceholder')"
          ></textarea>
          <small class="char-count">
            {{ formData.description.length }} / 1000 {{ $t('common.characters') }}
          </small>
        </div>
        
        <div class="form-actions">
          <AppButton type="button" variant="outline" @click="handleCancel">
            {{ $t('common.cancel') }}
          </AppButton>
          <AppButton type="submit" variant="primary" :disabled="!isFormValid">
            {{ $t('provisioning.submit') }}
          </AppButton>
        </div>
      </form>
    </AppModal>
  </teleport>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, watch } from 'vue';
import AppButton from '@/components/base/AppButton.vue';
import AppModal from '@/components/base/Modal.vue';
import type { ProvisioningRequestData } from '@/types/services';

interface Props {
  isVisible?: boolean;
}

interface Emits {
  (e: 'cancel'): void;
  (e: 'submit', payload: { data: ProvisioningRequestData; isValid: boolean }): void;
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: false
});

const emit = defineEmits<Emits>();

// Form data
const formData = ref<ProvisioningRequestData>({
  requestedAmount: 0,
  description: '',
  provisionDeadline: new Date().toISOString().split('T')[0]
});

// Computed
const minDate = computed(() => {
  // Deadline must be today or in the future
  return new Date().toISOString().split('T')[0];
});

const isFormValid = computed(() => {
  return (
    formData.value.requestedAmount > 0 &&
    formData.value.description.trim().length > 0 &&
    formData.value.description.length <= 1000 &&
    formData.value.provisionDeadline.length > 0
  );
});

// Methods
const handleCancel = () => {
  resetForm();
  emit('cancel');
};

const handleSubmit = () => {
  if (!isFormValid.value) {
    emit('submit', { data: formData.value, isValid: false });
    return;
  }
  
  emit('submit', { data: formData.value, isValid: true });
};

const resetForm = () => {
  formData.value = {
    requestedAmount: 0,
    description: '',
    provisionDeadline: new Date().toISOString().split('T')[0]
  };
};

// Watch for modal visibility changes to reset form
watch(() => props.isVisible, (newVal:boolean) => {
  if (newVal) {
    resetForm();
  }
});
</script>

<style scoped>
.finance-form {
  padding: 0;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-primary, #1f2937);
  font-size: 0.875rem;
}

.required {
  color: #ef4444;
  margin-left: 2px;
}

.form-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9375rem;
  transition: all 0.2s ease;
  background-color: #ffffff;
  color: #1f2937;
  box-sizing: border-box;
}

.form-input:hover {
  border-color: #9ca3af;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-input::placeholder {
  color: #9ca3af;
}

.form-input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

.form-input[type="number"]::-webkit-outer-spin-button,
.form-input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.form-input[type="date"] {
  font-family: inherit;
}

textarea.form-input {
  resize: vertical;
  min-height: 110px;
  max-height: 250px;
  font-family: inherit;
  line-height: 1.5;
}

.char-count {
  display: block;
  margin-top: 0.375rem;
  font-size: 0.75rem;
  color: #6b7280;
  text-align: right;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.75rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e5e7eb;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .form-group label {
    color: #f3f4f6;
  }
  
  .form-input {
    background-color: #374151;
    color: #f3f4f6;
    border-color: #4b5563;
  }
  
  .form-input:hover {
    border-color: #6b7280;
  }
  
  .form-input:focus {
    border-color: #60a5fa;
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.1);
  }
  
  .form-input::placeholder {
    color: #9ca3af;
  }
  
  .char-count {
    color: #9ca3af;
  }
  
  .form-actions {
    border-top-color: #4b5563;
  }
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .form-actions {
    flex-direction: column-reverse;
    gap: 0.5rem;
  }
  
  .form-actions button {
    width: 100%;
  }
}
</style>
